import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../Utils";
import { createFilterHandler } from "../../../../Utils/FilterUtils";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import { useBranch } from "../../../../Hooks/useBranch";
import { buildApiQuery } from "../../../../Utils/apiQueryBuilder";
import { useDelayPicking } from "../../../../Hooks/useDelayPicking";

const INITIAL_FILTER_STATE = {
  sssJobNo: "",
  custOrder: "",
  customer: "",
  picker: "",
  bay: "",
  orderDate: null,
  orderDateStart: null,
  orderDateEnd: null,
  freightLineOrder: "",
  cutoff: null,
  cutoffStart: null,
  cutoffEnd: null,
  status: "",
  orderLine: "",
  orderLineMin: "",
  orderLineMax: "",
  pickUpQty: "",
  pickUpQtyMin: "",
  pickUpQtyMax: "",
  reason: "",
  branch: null,
  packStation: "",
  git: "",
};

const fieldMappings = {
  sssJobNo: ["job_id", "$eq"],
  custOrder: ["order_no", "$eq"],
  customer: ["customer", "$eq"],
  // picker: ["picker", "$eq"],
  freightLineOrder: ["is_freight_line_order", "$eq"],
  bay: ["bin_bay", "$contains"],
  status: ["status", "$eq"],
  branch: ["branch", "$eq"],
  packStation: ["pack_station_id", "$eq"],
  git: ["git", ""],
};

const rangeFields = {
  orderDate: {
    apiField: "order_date",
    minField: "orderDateStart",
    maxField: "orderDateEnd",
  },
  cutoff: {
    apiField: "cutoff_date_time",
    minField: "cutoffStart",
    maxField: "cutoffEnd",
  },
  orderLine: {
    apiField: "total_order_lines",
    minField: "orderLineMin",
    maxField: "orderLineMax",
  },
  pickUpQty: {
    apiField: "pickup_qty",
    minField: "pickUpQtyMin",
    maxField: "pickUpQtyMax",
  },
};

const valueTransformers = {
  // Convert boolean active to 1/0
  active: (value) => {
    if (value === "Active") return 1;
    if (value === "Inactive") return 0;
    return value; // Return original value if not boolean
  },
  freightLineOrder: (value) => {
    if (value === "Yes") return 1;
    if (value === "No") return 0;
    return value;
  },
  orderDateStart: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
  orderDateEnd: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
};

// Derive initial state from URL
const getInitialOrder = (location) => {
  const path = location?.pathname;
  const searchParams = new URLSearchParams(location.search);

  if (searchParams.has("status") && searchParams.get("status") === "priority") {
    return "Priority";
  }

  if (path && path.includes("/orders/all")) return "All";
  if (path && path.includes("/orders/fake-customer-order"))
    return "Fake Customer";

  // Extract role from path
  const pathParts = path && path.split("/");
  if (pathParts && pathParts.length >= 3) {
    const orderSlug = pathParts[2];
    if (orderSlug === "git") {
      return "GIT";
    }
    const orderFormatted = orderSlug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    return orderFormatted;
  }
  return "All";
};

export const useOrdersData = ({
  isLatestOrders = false,
  type,
  onGetLatestOrders,
  shouldRefreshData = false,
}) => {
  const [orders, setOrders] = useState([]);
  const [activeStatus, setActiveStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState([]);
  const [pickers, setPickers] = useState([]);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingMarkAsCompleted, setIsSubmittingMarkAsCompleted] =
    useState(false);
  const [refreshOverviewTrigger, setRefreshOverviewTrigger] = useState(0);

   const [allowAssignPicker, setAllowAssignPicker] = useState(0);
  const [allowMarkAsComplete, setAllowMarkAsComplete] = useState(0);
  const [allowReleaseOrderAndAssignOrder, setAllowReleaseOrderAndAssignOrder] = useState(0);
  const [allowReleaseOrders, setAllowReleaseOrders] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { currentBranch } = useBranch();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh(); // to refresh after token expire
  const { setIsDelayPicking } = useDelayPicking();

  const endpoint = isLatestOrders
    ? "/dashboard/latest-orders"
    : `orders/${type}`;

  const fetchOrders = async (
    page = 1,
    pageSize = 20,
    filters = submittedData,
    sortInfo = sortModel
  ) => {
    try {
      setLoading(true);

      let usedFilters = { ...filters };

      const searchParams = new URLSearchParams(location.search);
      if (
        searchParams.has("status") &&
        searchParams.get("status") === "priority"
      ) {
        usedFilters.status = "priority";
      }

      const apiUrl = buildApiQuery(endpoint, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        filters: usedFilters,
        sortModel,
        fieldMappings,
        rangeFields,
        valueTransformers,
      });

      const res = await httpclient.get(apiUrl);
      const { data } = res;

      if (data.status === 200) {
        setOrders(data.data);
        setOrderStatus(data.orderStatus || []);
        setPickers(data.pickers || []);
        setRowCount(data.meta?.total || data.count || 0);



// setAllowAssignPicker(1); 
//   setAllowMarkAsComplete(1); 
//   setAllowReleaseOrderAndAssignOrder(1); 
//   setAllowReleaseOrders(1);


setAllowAssignPicker(data.allow_assign_picker || 0);
        setAllowMarkAsComplete(data.allow_mark_as_complete || 0);
        setAllowReleaseOrderAndAssignOrder(data.allow_release_order_and_assign_order || 0);
        setAllowReleaseOrders(data.allow_release_orders || 0);


        // Call the callback if it's latest orders
        if (isLatestOrders && onGetLatestOrders) {
          onGetLatestOrders(data.data);
        }
      } else {
        showNotification("error", data.message || "Failed to fetch orders");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(
      paginationModel.page + 1,
      paginationModel.pageSize,
      submittedData,
      sortModel
    );
  }, [
    currentBranch,
    type,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    shouldRefreshData,
    submittedData,
    location.search,
  ]);

  useEffect(() => {
    setActiveStatus(getInitialOrder(location));
  }, [location]);

  const handleRowClick = (params) => {
    // This function is now just returning the row data
    // The actual state update will happen in the parent component
    return params.row;
  };

  const handleChangeFilter = (field) => (event, value) => {
    if (
      field.includes("Date") ||
      field.includes("Start") ||
      field.includes("End")
    ) {
      setFilterData((prev) => ({
        ...prev,
        [field]: value || (event && event.target.value) || null,
      }));
      return;
    }

    if (field === "status") {
      setFilterData((prev) => ({
        ...prev,
        [field]: value || (event && event.target.value),
      }));
      return;
    }

    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setOrders,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: endpoint,
    fieldMappings,
    valueTransformers,
    rangeFields,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };
    newFilterData[field] =
      field.includes("Start") ||
      field.includes("End") ||
      field.includes("Min") ||
      field.includes("Max")
        ? null // Reset date/numeric range fields to null
        : ""; // Reset other fields to empty string
    delete newSubmittedData[field];
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    // Reset to the first page when sorting changes
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const executeReleaseOrderApi = async () => {
    if (selectedJobIds.length === 0) {
      // This check is more of a safeguard, as dialog shouldn't open without selection.
      showNotification("info", "No orders selected for release.");
      return;
    }
    setIsSubmitting(true);
    try {
      let releaseEndpoint = "";
      if (type === "delay-picking") {
        releaseEndpoint = "/orders/delay-picking-release";
      } else if (type === "git") {
        releaseEndpoint = "/orders/git-release";
      }

      if (!releaseEndpoint) {
        showNotification("error", "Invalid order type for release.");
        return;
      }

      const payload = { job_list: selectedJobIds };
      const res = await httpclient.post(releaseEndpoint, payload);

      if (res.data?.status === 200 || res.status === 200) {
        showNotification(
          "success",
          res.data?.message || "Orders released successfully!"
        );
        setSelectedJobIds([]);
        fetchOrders();
        if (type === "delay-picking") {
          setIsDelayPicking(true);
        }
      } else {
        showNotification(
          "error",
          res.data?.message || "Failed to release orders."
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenReleaseOrdersDialog = () => {
    if (selectedJobIds.length === 0) {
      showNotification("info", "Please select orders to release.");
      return;
    }
    return true;
  };

  const handleConfirmReleaseOrders = () => {
    executeReleaseOrderApi(); // Execute the API call
  };

  const handleOpenAssignPickerDialog = () => {
    if (selectedJobIds.length === 0) {
      showNotification("info", "Please select orders to assign a picker.");
      return;
    }
    return true;
  };

  const handleOpenMarkAsCompletedDialog = () => {
    if (selectedJobIds.length === 0) {
      showNotification("info", "Please select orders to mark as completed.");
      return;
    }
    return true;
  };

  const handleMarkAsCompleted = async () => {
    if (selectedJobIds.length === 0) {
      // This check is more of a safeguard, as dialog shouldn't open without selection.
      showNotification("info", "No orders selected to mark as completed.");
      return;
    }

    setIsSubmittingMarkAsCompleted(true);

    try {
      const payload = { job_list: selectedJobIds };
      const res = await httpclient.post(
        "/orders/complete-in-queue-order",
        payload
      );

      if (res.data?.status === 200 || res.status === 200) {
        showNotification(
          "success",
          res.data?.message ||
            `${
              selectedJobIds.length === 1 ? "Order" : "Orders"
            } mardked as completed successfully!`
        );
        setSelectedJobIds([]);
        fetchOrders();
      } else {
        showNotification(
          "error",
          res.data?.message || "Failed to mark as completed successfully"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setIsSubmittingMarkAsCompleted(false);
    }
  };

  const triggerOverviewRefresh = () => {
    setRefreshOverviewTrigger((prev) => prev + 1); // Increment to trigger effect
  };

  // Function to handle status label click from LeftStats
  // This mimics the logic inside StatusTabs
  const handleStatusLabelClick = (statusValue) => {
    setActiveStatus(statusValue);

    // Perform navigation based on the status value
    // This logic should match the handleFilterClick in StatusTabs
    let path = "";
    if (statusValue === "All") {
      path = "/orders/all";
    } else if (statusValue === "Priority") {
      path = "/orders/in-queue?status=priority";
    } else {
      // Convert statusValue to slug format (e.g., "In Progress" -> "in-progress")
      // Ensure this matches exactly how StatusTabs constructs paths
      const slug = statusValue.toLowerCase().replace(/ /g, "-");
      path = `/orders/${slug}`;
    }

    navigate(path);

    // Optionally, trigger a refresh of the overview stats if needed immediately
    // Otherwise, the useEffect in OrdersOverview triggered by URL/location change should suffice
    // triggerOverviewRefresh(); // Uncomment if immediate refresh is needed
  };

  const gridRows = useMemo(() => {
    if (!orders?.length) return [];

    // The server handles primary sorting, but we can apply a secondary sort here for priority orders.
    const sortedOrders = [...orders].sort(
      (a, b) => (b.isPriorityOrder === true) - (a.isPriorityOrder === true)
    );

    return sortedOrders.map((row) => ({
      ...row,
      // Aliases for DataGrid columns for consistency
      sssJobNo: row.job_id,
      custOrder: row.order_no,
      orderDate: row.order_date,
      cutoff: row.cutoff_date_time,
    }));
  }, [orders]);

  const hasReason = useMemo(() => {
    return (
      orders.some(
        (row) => row.status === "On Hold" || row.status === "Canceled"
      ) || type === "fake-customer-order"
    );
  }, [orders, type]);

  const shouldHideStatus = useMemo(() => {
    return (
      type === "delay-picking" ||
      type === "git" ||
      type === "fake-customer-order"
    );
  }, [type]);

  const isDelayPicking = useMemo(() => {
    return type === "delay-picking";
  }, [type]);

  const isFakeCustomerOrder = useMemo(() => {
    return type === "fake-customer-order";
  }, [type]);

  const tooltipMessage = useMemo(() => {
    return selectedJobIds.length === 0
      ? `Select at least one order to ${
          type === "in-queue" ? "assign to a picker" : "release"
        }`
      : isSubmitting
      ? "Submitting..."
      : "";
  }, [selectedJobIds, type, isSubmitting]);

  const isCheckBoxSelection = useMemo(() => {
    return type === "delay-picking" || type === "git" || type === "in-queue";
  }, [type]);

  return {
    // State values
    orders,
    activeStatus,
    orderStatus,
    pickers,
    filterData,
    submittedData,
    selectedJobIds,
    loading,
    isSubmitting,
    isSubmittingMarkAsCompleted,
    refreshOverviewTrigger,
    paginationModel,
    rowCount,
    sortModel,
    gridRows,
    hasReason,
    shouldHideStatus,
    isDelayPicking,
    isFakeCustomerOrder,
    tooltipMessage,
    isCheckBoxSelection,

//  Permission flags from API response
     allowAssignPicker,
    allowMarkAsComplete,
    allowReleaseOrderAndAssignOrder,
    allowReleaseOrders,

    // State setters
    setActiveStatus,
    setFilterData,
    setSubmittedData,
    setSelectedJobIds,
    setPaginationModel,
    setSortModel,

    // Functions
    fetchOrders,
    handleRowClick,
    handleChangeFilter,
    onFilterClick,
    handleRemove,
    handleClearFilters,
    handleSortModelChange,
    executeReleaseOrderApi,
    handleOpenReleaseOrdersDialog,
    handleConfirmReleaseOrders,
    handleOpenAssignPickerDialog,
    handleOpenMarkAsCompletedDialog,
    handleMarkAsCompleted,
    triggerOverviewRefresh,
    handleStatusLabelClick,
  };
};
